import axiosInstance from './axiosInstance'

// ── Types ─────────────────────────────────────────────────────────────────────

export type UserStatus = 'Active' | 'Inactive' | 'Suspended'

export interface AdminUser {
  id: string
  name: string
  email: string
  phone: string | null
  status: UserStatus
  isEmailVerified: boolean
  roleId: string
  roleName: string
  orgId: string | null
  lobId: string | null
  warehouseId: string | null
  orgName: string | null
  lobName: string | null
  warehouseName: string | null
  /** C-Zentrix agent ID for this user. Set by admin. Used for click-to-dial. */
  czentrixAgentId: number | null
  /** Whether this user is eligible for new lead assignments (Round Robin / Least Loaded / Team). */
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface PaginatedUsers {
  items: AdminUser[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface CreateUserPayload {
  name: string
  email: string
  phone?: string
  status?: UserStatus
  roleId: string
  orgId?: string
  lobId?: string
  warehouseId?: string
  isActive?: boolean
}

export interface UpdateUserPayload {
  name?:             string
  phone?:            string
  status?:           UserStatus
  roleId?:           string
  orgId?:            string
  lobId?:            string
  warehouseId?:      string
  czentrixAgentId?:  number | null
  isActive?:         boolean
}

export interface UserFilters {
  page?: number
  pageSize?: number
  search?: string
  roleId?: string
  status?: string
  orgId?: string | null
  lobId?: string | null
}

// ── API ───────────────────────────────────────────────────────────────────────

export const usersAdminApi = {
  getAll: async (filters: UserFilters = {}): Promise<PaginatedUsers> => {
    const params = new URLSearchParams()
    if (filters.page) params.set('page', String(filters.page))
    params.set('pageSize', String(filters.pageSize ?? 1000))
    if (filters.search)   params.set('search',   filters.search)
    if (filters.roleId)   params.set('roleId',   filters.roleId)
    if (filters.status)   params.set('status',   filters.status)
    if (filters.orgId)    params.set('orgId',    filters.orgId)
    if (filters.lobId)    params.set('lobId',    filters.lobId)
    const res = await axiosInstance.get<{ data: PaginatedUsers }>('/users?' + params.toString())
    return res.data.data
  },

  getById: async (id: string): Promise<AdminUser> => {
    const res = await axiosInstance.get<{ data: AdminUser }>('/users/' + id)
    return res.data.data
  },

  create: async (payload: CreateUserPayload): Promise<AdminUser> => {
    const res = await axiosInstance.post<{ data: AdminUser }>('/users', payload)
    return res.data.data
  },

  update: async (id: string, payload: UpdateUserPayload): Promise<AdminUser> => {
    const res = await axiosInstance.put<{ data: AdminUser }>('/users/' + id, payload)
    return res.data.data
  },

  assignRole: async (id: string, roleId: string): Promise<AdminUser> => {
    const res = await axiosInstance.put<{ data: AdminUser }>('/users/' + id + '/role', { roleId })
    return res.data.data
  },

  remove: async (id: string): Promise<void> => {
    await axiosInstance.delete('/users/' + id)
  },
}
